"use client";

import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  memo,
} from "react";
import {
  X, Check, Loader2, Lock, ShoppingBasket,
  Heart, RefreshCw, Shield, Star, Minus, Plus,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  Maximize2, Share2, ExternalLink,
} from "lucide-react";
import Link from "next/link";
import ProductGallery from "@/lib/ProductGallery";
import {
  updateLocalCartVariant,
  addToLocalCart,
  getLocalCart,
} from "@/lib/cartUtils";
import SizeChartModal from "@/lib/SizeChartModal";

// ─── Config ───────────────────────────────────────────────────────────────────
const API_BASE    = process.env.NEXT_PUBLIC_API_URL    || "/api";
const SERVER_BASE = process.env.NEXT_PUBLIC_SERVER_BASE || "";

function getToken(): string {
  if (typeof window === "undefined") return "";
  const KEYS = ["auth_token", "token", "authToken", "jwt", "access_token", "accessToken"];
  for (const k of KEYS) {
    const v = localStorage.getItem(k) ?? sessionStorage.getItem(k);
    if (v) return v;
  }
  return "";
}

async function apiFetch<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { message?: string }).message ?? `HTTP ${res.status}`);
  return body as T;
}

function resolveImg(src: string | undefined | null): string {
  if (!src) return FALLBACK;
  if (src.startsWith("http") || src.startsWith("data:")) return src;
  return `${SERVER_BASE}${src}`;
}

const FALLBACK =
  "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=600&q=80";

// ─── Types (mirrors women/page.tsx) ──────────────────────────────────────────
type MediaItem         = { type: "img"; src: string; alt?: string; is_primary?: boolean };
type VariantColorImage = { image_url: string; alt_text?: string; is_primary: boolean; sort_order: number };
type VariantColor      = { value: string; hex: string; stock_qty: number; images?: VariantColorImage[] };
type VariantSize       = { value: string; stock_qty: number };
type VariantItem       = { size: string; color: string; hex: string; stock_qty: number };
type Variants          = { sizes: VariantSize[]; colors: VariantColor[]; items: VariantItem[] };

type Product = {
  id:                number;
  name:              string;
  slug:              string;
  sku:               string;
  category:          string;
  parent_category?:  string;
  short_description: string | null;
  material:          string | null;
  regular_price:     number;
  sale_price:        number | null;
  currency:          string;
  stock:             number;
  rating_avg:        number;
  rating_count:      number;
  is_featured:       boolean;
  is_trending:       boolean;
  is_new:            boolean;
  is_best_seller:    boolean;
  status:            string;
  media:             MediaItem[];
  images:            MediaItem[];
  variants?:         Variants;
  gender?:            string | null;
};

export interface CartItem {
  cartItemId:      number;
  productId:       number;
  variantId?:      number | null;
  title:           string;
  sku:             string | null;
  color:           string | null;
  size:            string | null;
  price:           number;
  salePrice:       number | null;
  discountPercent?: number;
  quantity:        number;
  lineTotal:       number;
  image:           string | null;
  colorImage?:     string | null;
  selectedImage?:  string | null;
  inStock:         boolean;
  stock?:          number;
  variantHex?:     string | null;
  brand?:          string | null;
  rating?:         number | null;
  reviewCount?:    number | null;
  material?:       string | null;
  slug?:           string | null;
  delivery?:       string;
  returnPolicy?:   string;
  badges?: {
    isNew:        boolean;
    isTrending:   boolean;
    isFeatured:   boolean;
    isBestSeller: boolean;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function sortColorImages(images?: VariantColorImage[]): VariantColorImage[] {
  if (!Array.isArray(images) || !images.length) return [];
  return [...images].sort((a, b) => {
    if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });
}

function normaliseImages(p: any): MediaItem[] {
  const raw: any[] = Array.isArray(p.images) && p.images.length ? p.images
    : Array.isArray(p.media) ? p.media : [];
  return [...raw]
    .sort((a, b) => {
      if (!!a.is_primary !== !!b.is_primary) return a.is_primary ? -1 : 1;
      return (Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)) || (Number(a.id ?? 0) - Number(b.id ?? 0));
    })
    .map((m) => ({ type: "img" as const, src: m.src ?? m.image_url, alt: m.alt ?? m.alt_text, is_primary: !!m.is_primary }));
}

function normaliseVariants(p: any): Variants {
  const items: VariantItem[] = Array.isArray(p.variants?.items)
    ? p.variants.items.filter((it: VariantItem) => it.size && it.color) : [];

  const rawColors: any[] = Array.isArray(p.variants?.colors) ? p.variants.colors : [];
  const galleryByColor = new Map<string, VariantColorImage[]>();
  for (const c of rawColors) {
    const key = c?.value ?? c?.name;
    if (key) galleryByColor.set(key, sortColorImages(c.images));
  }

  if (!items.length) return { sizes: [], colors: [], items: [] };

  const sizeMap = new Map<string, number>();
  for (const it of items) sizeMap.set(it.size, (sizeMap.get(it.size) ?? 0) + (Number(it.stock_qty) || 0));
  const sizes: VariantSize[] = Array.from(sizeMap, ([value, stock_qty]) => ({ value, stock_qty }));

  const colorMap = new Map<string, { hex: string; stock_qty: number }>();
  for (const it of items) {
    const prev = colorMap.get(it.color);
    colorMap.set(it.color, { hex: it.hex ?? prev?.hex ?? "#888", stock_qty: (prev?.stock_qty ?? 0) + (Number(it.stock_qty) || 0) });
  }

  const colors: VariantColor[] = Array.from(colorMap, ([value, { hex, stock_qty }]) => {
    const imgs = galleryByColor.get(value);
    return imgs?.length ? { value, hex, stock_qty, images: imgs } : { value, hex, stock_qty };
  });

  return { sizes, colors, items: items.map((it) => ({ size: it.size, color: it.color, hex: it.hex ?? "#888", stock_qty: Number(it.stock_qty) || 0 })) };
}

function resolveSizeForColor(variants: Variants | undefined, color: string, currentSize: string): string {
  const sizes = variants?.sizes ?? [];
  const items = variants?.items ?? [];
  if (!sizes.length) return "";
  if (items.length) {
    if (currentSize && items.some((it) => it.color === color && it.size === currentSize && it.stock_qty > 0)) return currentSize;
    const next = items.find((it) => it.color === color && it.stock_qty > 0);
    return next?.size ?? "";
  }
  if (currentSize && sizes.some((s) => s.value === currentSize && s.stock_qty > 0)) return currentSize;
  return sizes.find((s) => s.stock_qty > 0)?.value ?? "";
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function ModalSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 animate-pulse">
      <div className="p-4 md:p-6 border-b lg:border-b-0 lg:border-r border-[#ece7df]">
        <div className="bg-[#f0ebe3] rounded-xl" style={{ minHeight: 480 }} />
        <div className="flex gap-2 mt-3">
          {[1, 2, 3].map((n) => <div key={n} className="w-[72px] h-[86px] bg-[#f0ebe3] rounded-md" />)}
        </div>
      </div>
      <div className="p-6 md:p-8 space-y-4">
        <div className="h-3 bg-[#f0ebe3] rounded w-1/3" />
        <div className="h-8 bg-[#f0ebe3] rounded w-3/4" />
        <div className="h-4 bg-[#f0ebe3] rounded w-1/4" />
        <div className="h-20 bg-[#f0ebe3] rounded" />
        <div className="h-32 bg-[#f0ebe3] rounded" />
        <div className="h-12 bg-[#f0ebe3] rounded" />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
interface CartProductModalProps {
  cartItem:           CartItem;
  onClose:            () => void;
  onCartUpdated:      (updatedItem: Partial<CartItem> & { cartItemId: number }) => void;
  onNewVariantAdded:  (newItem: CartItem) => void;
}

export default function CartProductModal({
  cartItem,
  onClose,
  onCartUpdated,
  onNewVariantAdded,
}: CartProductModalProps) {
  // ── Product fetch state ───────────────────────────────────────────────────
  const [product,  setProduct]  = useState<Product | null>(null);
  const [fetching, setFetching] = useState(true);
  const [fetchErr, setFetchErr] = useState<string | null>(null);

  // ── Selection state (seeded from cart item) ───────────────────────────────
  const [selColor, setSelColor] = useState(cartItem.color ?? "");
  const [selSize,  setSelSize]  = useState(cartItem.size  ?? "");
  const [qty,      setQty]      = useState(cartItem.quantity);

  // ── Action state ──────────────────────────────────────────────────────────
  const [saving,         setSaving]         = useState(false);
  const [addingVariant,  setAddingVariant]  = useState(false);
  const [actionMsg,      setActionMsg]      = useState<{ text: string; ok: boolean } | null>(null);

  // ── Fetch full product on mount ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    setFetchErr(null);

    apiFetch<{ success: boolean; data: any }>(`/products/${cartItem.productId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const normalised: Product = {
          ...data,
          gender:         data.gender ?? data.PRODUCT_TYPE_OPTIONS ?? null,
          stock:          Number(data.stock ?? data.stock_qty ?? 0),
          is_featured:    !!data.is_featured,
          is_trending:    !!data.is_trending,
          is_new:         !!data.is_new,
          is_best_seller: !!data.is_best_seller,
          media:          (data.media ?? []).map((m: any) => ({ type: "img" as const, src: m.src, alt: m.alt, is_primary: !!m.is_primary })),
          images:         normaliseImages(data),
          variants:       normaliseVariants(data),
        };
        setProduct(normalised);
      })
      .catch((err) => { if (!cancelled) setFetchErr(err.message); })
      .finally(() => { if (!cancelled) setFetching(false); });

    return () => { cancelled = true; };
  }, [cartItem.productId]);

  // ── Re-seed selections whenever the cart item prop changes ────────────────
  useEffect(() => {
    setSelColor(cartItem.color ?? "");
    setSelSize(cartItem.size  ?? "");
    setQty(cartItem.quantity);
  }, [cartItem.cartItemId, cartItem.color, cartItem.size, cartItem.quantity]);

  // ── Lock scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // ── Keyboard dismiss ──────────────────────────────────────────────────────
  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  // ── Derived gallery images (color-aware, same logic as women/page.tsx) ────
  const galleryImages = useMemo(() => {
    const source = product?.images?.length ? product.images : product?.media;
    if (!source?.length) return [FALLBACK];
    return source.map((m) => resolveImg(m.src)).filter(Boolean);
  }, [product?.images, product?.media]);

  const activeColor = useMemo(() => {
    if (!product?.variants?.colors?.length) return undefined;
    return product.variants.colors.find((c) => c.value === selColor) ?? product.variants.colors[0];
  }, [product?.variants, selColor]);

  const activeImages = useMemo(() => {
    const colorImgs = activeColor?.images?.length
      ? sortColorImages(activeColor.images).map((img) => resolveImg(img.image_url)).filter(Boolean)
      : [];
    const merged = [...colorImgs];
    for (const src of galleryImages) { if (!merged.includes(src)) merged.push(src); }
    return merged.length ? merged : [FALLBACK];
  }, [activeColor, galleryImages]);

  // ── Availability ──────────────────────────────────────────────────────────
  const availableStock = useMemo((): number => {
    if (!product) return 0;
    if (selSize && selColor) {
      const combo = product.variants?.items?.find((it) => it.size === selSize && it.color === selColor);
      if (combo !== undefined) return Number(combo.stock_qty) || 0;
    }
    if (selSize)  { const s = product.variants?.sizes.find((s) => s.value === selSize);  if (s) return s.stock_qty; }
    if (selColor) { const c = product.variants?.colors.find((c) => c.value === selColor); if (c) return c.stock_qty; }
    return product.stock;
  }, [product, selSize, selColor]);

  const hasSizes  = (product?.variants?.sizes  ?? []).length > 0;
  const hasColors = (product?.variants?.colors ?? []).length > 0;

  const selectionState: "idle" | "needColor" | "needSize" | "ready" = (() => {
    if (hasColors && !selColor) return "needColor";
    if (hasSizes  && !selSize)  return "needSize";
    return "ready";
  })();

  const canAct = selectionState === "ready" && availableStock > 0;

  const variantChanged =
    selColor !== (cartItem.color ?? "") ||
    selSize  !== (cartItem.size  ?? "") ||
    qty      !== cartItem.quantity;

  const discount = product?.sale_price
    ? Math.round((1 - Number(product.sale_price) / Number(product.regular_price)) * 100) : 0;

  // ── Color change (keeps size if still valid) ──────────────────────────────
  const handleColorSelect = (value: string) => {
    const next = selColor === value ? "" : value;
    setSelColor(next);
    if (next) setSelSize((prev) => resolveSizeForColor(product?.variants, next, prev));
    else setSelSize("");
  };

  // ── Flash message helper ──────────────────────────────────────────────────
  const flash = (text: string, ok: boolean) => {
    setActionMsg({ text, ok });
    setTimeout(() => setActionMsg(null), 3000);
  };

const isGuest = cartItem.cartItemId < 0;
const [showSizeChart, setShowSizeChart] = useState(false);

  // ─── Update Cart (PATCH existing item) ────────────────────────────────────
  const handleUpdateCart = async () => {
  if (!canAct) return;
  setSaving(true);
  try {
    const resolvedImage = activeImages[0] ?? cartItem.image ?? null;

    if (isGuest) {
      const updated = updateLocalCartVariant(cartItem.cartItemId, {
        color:          selColor || null,
        size:           selSize  || null,
        quantity:       qty,
        price:          Number(product?.regular_price ?? cartItem.price),
        salePrice:      product?.sale_price ? Number(product.sale_price) : null,
        image:          resolvedImage,
        colorImage:     resolvedImage,
        selectedImage:  resolvedImage,
        gallery:        activeImages,
        stock:          availableStock,
        inStock:        availableStock > 0,
        variantHex:     activeColor?.hex ?? null,
        sku:            product?.sku ?? cartItem.sku,
        discountPercent: discount,
      });
      if (updated) onCartUpdated({ ...updated, cartItemId: cartItem.cartItemId });
      flash("Cart updated!", true);
      return;
    }

    const res = await apiFetch<{ success: boolean; data: any; message: string }>(
      `/cart/item/${cartItem.cartItemId}/variant`,
      {
        method: "PATCH",
        body: JSON.stringify({ color: selColor || null, size: selSize || null, quantity: qty }),
      }
    );
    if (!res.success) throw new Error(res.message);
    onCartUpdated({ ...res.data, cartItemId: cartItem.cartItemId });
    flash("Cart updated!", true);
  } catch (err: unknown) {
    flash(err instanceof Error ? err.message : "Could not update cart.", false);
  } finally {
    setSaving(false);
  }
};

  // ─── Add as New Variant (POST new line) ───────────────────────────────────
  const handleAddAsNewVariant = async () => {
  if (!canAct) return;
  setAddingVariant(true);
  try {
    const resolvedImage = activeImages[0] ?? cartItem.image ?? null;

    if (isGuest) {
      const newId = addToLocalCart({
        productId:     cartItem.productId,
        title:         product?.name ?? cartItem.title,
        sku:           product?.sku  ?? cartItem.sku,
        color:         selColor || null,
        size:          selSize  || null,
        price:         Number(product?.regular_price ?? cartItem.price),
        salePrice:     product?.sale_price ? Number(product.sale_price) : null,
        quantity:      qty,
        image:         resolvedImage,
        colorImage:    resolvedImage,
        selectedImage: resolvedImage,
        gallery:       activeImages,
        inStock:       availableStock > 0,
        stock:         availableStock,
        variantHex:    activeColor?.hex ?? null,
        material:      product?.material ?? cartItem.material,
        slug:          product?.slug ?? cartItem.slug,
        delivery:      cartItem.delivery,
        returnPolicy:  cartItem.returnPolicy,
        discountPercent: discount,
        rating:        product?.rating_avg   ?? cartItem.rating ?? null,
        reviewCount:   product?.rating_count ?? cartItem.reviewCount ?? null,
        badges: {
          isNew:        !!product?.is_new,
          isTrending:   !!product?.is_trending,
          isFeatured:   !!product?.is_featured,
          isBestSeller: !!product?.is_best_seller,
        },
      });
      const stored = getLocalCart().find((i) => i.cartItemId === newId);
      if (stored) onNewVariantAdded({ ...(stored as unknown as CartItem), cartItemId: stored.cartItemId });
      flash("Added as a new variant!", true);
      setTimeout(onClose, 1400);
      return;
    }

    const res = await apiFetch<{ success: boolean; data: any; message: string }>(
      "/cart/add",
      {
        method: "POST",
        body: JSON.stringify({
          product_id: cartItem.productId,
          color:      selColor || null,
          size:       selSize  || null,
          quantity:   qty,
        }),
      }
    );
    if (!res.success) throw new Error(res.message);

    const newItem: CartItem = {
      cartItemId:     res.data.cartItemId,
      productId:      cartItem.productId,
      variantId:      res.data.variantId ?? null,
      title:          product?.name ?? cartItem.title,
      sku:            product?.sku  ?? cartItem.sku,
      color:          selColor || null,
      size:           selSize  || null,
      price:          Number(product?.regular_price ?? cartItem.price),
      salePrice:      product?.sale_price ? Number(product.sale_price) : null,
      quantity:       qty,
      lineTotal:      (product?.sale_price ?? product?.regular_price ?? cartItem.price) * qty,
      image:          res.data.colorImage ?? resolvedImage,
      colorImage:     res.data.colorImage ?? null,
      selectedImage:  res.data.colorImage ?? null,
      inStock:        availableStock > 0,
      stock:          availableStock,
      variantHex:     activeColor?.hex ?? null,
      brand:          cartItem.brand,
      material:       product?.material ?? cartItem.material,
      slug:           product?.slug ?? cartItem.slug,
      delivery:       cartItem.delivery,
      returnPolicy:   cartItem.returnPolicy,
    };

    onNewVariantAdded(newItem);
    flash("Added as a new variant!", true);
    setTimeout(onClose, 1400);
  } catch (err: unknown) {
    flash(err instanceof Error ? err.message : "Could not add variant.", false);
  } finally {
    setAddingVariant(false);
  }
};

  // ─── Share ────────────────────────────────────────────────────────────────
  const handleShare = () => {
    const url = product?.slug ? `${window.location.origin}/shop/women` : window.location.href;
    if (navigator.share) {
      navigator.share({ title: product?.name, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => flash("Link copied!", true)).catch(() => {});
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-end sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Product details — ${cartItem.title}`}
    >
      <div
        className="bg-white w-full max-w-5xl max-h-[96vh] sm:max-h-[92vh] overflow-y-auto sm:rounded-2xl rounded-t-2xl shadow-2xl relative flex flex-col"
        style={{ animation: "cartModalIn 0.28s cubic-bezier(0.34,1.05,0.64,1) forwards" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white border border-[#e8e4dd] text-[#555] hover:text-black hover:border-black transition-colors flex items-center justify-center shadow-sm"
        >
          <X size={16} />
        </button>

        {/* ── Loading ── */}
        {fetching && (
          <div className="p-6">
            <ModalSkeleton />
          </div>
        )}

        {/* ── Error ── */}
        {!fetching && fetchErr && (
          <div className="flex flex-col items-center justify-center py-24 gap-4 px-6">
            <p className="font-['DM_Mono'] text-[13px] text-red-500">{fetchErr}</p>
            <button
              onClick={() => window.location.reload()}
              className="font-['DM_Mono'] text-[12px] text-[#b8955a] underline"
            >
              Reload
            </button>
          </div>
        )}

        {/* ── Product content ── */}
        {!fetching && !fetchErr && product && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 flex-1">

              {/* Gallery column */}
              <div className="p-4 md:p-6 border-b lg:border-b-0 lg:border-r border-[#ece7df] flex flex-col">
                <div style={{ minHeight: 420, flex: 1 }}>
                  <ProductGallery images={activeImages} productName={product.name} />
                </div>
              </div>

              {/* Details column */}
              <div className="p-6 md:p-8 overflow-y-auto">

                {/* Breadcrumb */}
                {product.category && (
                  <p className="font-['DM_Mono'] text-[12px] tracking-[0.2em] uppercase text-[#b8955a] mb-2">
                    {product.parent_category ? `${product.parent_category} › ` : ""}{product.category}
                  </p>
                )}

                <h2 className="font-['Cormorant_Garamond'] italic font-light text-[2rem] md:text-[2.2rem] leading-tight text-[#1a1a1a] mb-3">
                  {product.name}
                </h2>

                {/* Rating */}
                {product.rating_count > 0 && (
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[#b8955a] text-[15px] tracking-[2px]">
                      {"★".repeat(Math.round(Number(product.rating_avg)))}
                      {"☆".repeat(5 - Math.round(Number(product.rating_avg)))}
                    </span>
                    <span className="font-['DM_Mono'] text-[13px] text-[#888]">
                      ({product.rating_count} review{product.rating_count !== 1 ? "s" : ""})
                    </span>
                  </div>
                )}

                {/* Price */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-['DM_Mono'] text-[1.8rem] text-[#1a1a1a]">
                    ₹{(product.sale_price ?? product.regular_price).toLocaleString()}
                  </span>
                  {product.sale_price && (
                    <>
                      <span className="font-['DM_Mono'] text-[15px] text-[#aaa] line-through">
                        ₹{Number(product.regular_price).toLocaleString()}
                      </span>
                      <span className="bg-red-100 text-red-600 text-[11px] font-bold px-2 py-0.5 rounded font-['DM_Mono']">
                        -{discount}% OFF
                      </span>
                    </>
                  )}
                </div>

                {/* Short description */}
                {product.short_description && (
                  <div className="border-t border-b border-[#eee7de] py-4 my-4">
                    <p className="font-['DM_Mono'] text-[13px] leading-[1.9] text-[#333] tracking-wide">
                      {product.short_description}
                    </p>
                  </div>
                )}

                {/* Meta badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {product.is_new         && <span className="bg-blue-100 text-blue-700 text-[11px] font-bold px-2.5 py-1 rounded font-['DM_Mono'] uppercase tracking-wide">New Arrival</span>}
                  {product.is_featured    && <span className="bg-amber-100 text-amber-700 text-[11px] font-bold px-2.5 py-1 rounded font-['DM_Mono'] uppercase tracking-wide">★ Featured</span>}
                  {product.is_best_seller && <span className="bg-emerald-100 text-emerald-700 text-[11px] font-bold px-2.5 py-1 rounded font-['DM_Mono'] uppercase tracking-wide">Best Seller</span>}
                  {product.is_trending    && <span className="bg-purple-100 text-purple-700 text-[11px] font-bold px-2.5 py-1 rounded font-['DM_Mono'] uppercase tracking-wide">Trending</span>}
                  {product.material       && <span className="bg-[#f5f0e8] text-[#8a7560] text-[11px] px-2.5 py-1 rounded font-['DM_Mono'] uppercase tracking-wide">{product.material}</span>}
                </div>

                <div className="flex items-center justify-between mb-3">
                <span className="font-['DM_Mono'] text-[13px] uppercase tracking-[0.12em] text-[#333] flex items-center gap-2">
                  {/* ...unchanged icon/label... */}
                  Size
                  {hasColors && !selColor && <span className="font-['DM_Mono'] text-[9px] text-[#ccc] normal-case tracking-normal">(select colour first)</span>}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowSizeChart(true)}
                    className="font-['DM_Mono'] text-[10px] uppercase tracking-wide text-[#b8955a] underline underline-offset-2 hover:text-[#1a1a1a] transition-colors"
                  >
                    Size Chart
                  </button>
                  {selSize ? (
                    <span className="font-['DM_Mono'] text-[12px] text-emerald-600 font-medium">{selSize} — {product.variants!.sizes.find((s) => s.value === selSize)?.stock_qty ?? 0} in stock ✓</span>
                  ) : (
                    <span className="font-['DM_Mono'] text-[12px] text-[#bbb]">Select size</span>
                  )}
                </div>
              </div>

              {showSizeChart && (
              <SizeChartModal
                gender={product.gender ?? "women"}
                
                onClose={() => setShowSizeChart(false)}
              />
            )}

                {/* Current cart selection banner */}
                <div className="bg-[#faf7f2] border border-[#e8d9b8] rounded-xl px-4 py-3 mb-5">
                  <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.15em] text-[#b8955a] mb-1.5">Currently in your bag</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {cartItem.color && (
                      <span className="flex items-center gap-1.5 font-['DM_Mono'] text-[12px] text-[#555]">
                        {cartItem.variantHex && (
                          <span className="w-3.5 h-3.5 rounded-full border border-[#ccc]" style={{ backgroundColor: cartItem.variantHex }} />
                        )}
                        {cartItem.color}
                      </span>
                    )}
                    {cartItem.size && (
                      <span className="font-['DM_Mono'] text-[12px] text-[#555] border border-[#ddd] px-2 py-0.5 rounded">
                        {cartItem.size}
                      </span>
                    )}
                    <span className="font-['DM_Mono'] text-[12px] text-[#555]">
                      Qty: {cartItem.quantity}
                    </span>
                  </div>
                </div>

                {/* Step guide */}
                {selectionState !== "ready" && (
                  <div className="flex items-center gap-2 bg-[#fdf8f0] border border-[#e8d9b8] rounded-xl px-4 py-3 mb-4">
                    <span className="text-[#b8955a] text-[14px]">→</span>
                    <p className="font-['DM_Mono'] text-[12px] text-[#8a6a30] tracking-wide font-medium">
                      {selectionState === "needColor" ? "Step 1 — Choose a colour to continue" : "Step 2 — Now choose your size"}
                    </p>
                  </div>
                )}

                {/* ── Color selector ── */}
                {hasColors && (
                  <div className={`border-2 rounded-xl p-4 mb-3 transition-all duration-300 ${
                    selectionState === "needColor" ? "border-[#b8955a] bg-[#fdf8f0] shadow-[0_0_0_3px_rgba(184,149,90,0.12)]"
                    : selColor ? "border-emerald-300 bg-emerald-50/30" : "border-[#e8e4dd]"
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-['DM_Mono'] text-[13px] uppercase tracking-[0.12em] text-[#333] flex items-center gap-2">
                        {selColor
                          ? <Check size={13} className="text-emerald-500" strokeWidth={3} />
                          : <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[9px] font-bold ${selectionState === "needColor" ? "border-[#b8955a] text-[#b8955a]" : "border-[#ddd] text-[#ddd]"}`}>1</span>}
                        Colour
                      </span>
                      <span className={`font-['DM_Mono'] text-[13px] capitalize font-medium ${selColor ? "text-emerald-600" : "text-[#bbb]"}`}>
                        {selColor ? `${selColor} ✓` : "Select colour"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {product.variants!.colors.map((c) => {
                        const oos = c.stock_qty === 0;
                        const sel = selColor === c.value;
                        return (
                          <button
                            key={c.value}
                            onClick={() => { if (!oos) handleColorSelect(c.value); }}
                            disabled={oos}
                            className={`group flex flex-col items-center gap-1.5 transition-all ${oos ? "cursor-not-allowed opacity-30" : "cursor-pointer"}`}
                          >
                            <div
                              className={`relative w-9 h-9 rounded-full border-2 transition-all ${
                                sel ? "border-[#1a1a1a] scale-110 shadow-md" : "border-[#ddd] hover:border-[#999] hover:scale-105"
                              }`}
                              style={{ backgroundColor: c.hex }}
                            >
                              {sel && <span className="absolute inset-0 flex items-center justify-center"><Check size={13} className="text-white drop-shadow" strokeWidth={3} /></span>}
                            </div>
                            <span className={`font-['DM_Mono'] text-[9px] capitalize ${sel ? "text-[#b8955a] font-bold" : "text-[#bbb]"}`}>
                              {c.value}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Size selector ── */}
                {hasSizes && (
                  <div className={`border-2 rounded-xl p-4 mb-3 transition-all duration-300 ${
                    selectionState === "needSize" ? "border-[#b8955a] bg-[#fdf8f0] shadow-[0_0_0_3px_rgba(184,149,90,0.12)]"
                    : selSize ? "border-emerald-300 bg-emerald-50/30"
                    : hasColors && !selColor ? "border-[#eee] opacity-50 pointer-events-none"
                    : "border-[#e8e4dd]"
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-['DM_Mono'] text-[13px] uppercase tracking-[0.12em] text-[#333] flex items-center gap-2">
                        {selSize
                          ? <Check size={13} className="text-emerald-500" strokeWidth={3} />
                          : hasColors && !selColor ? <Lock size={11} className="text-[#ccc]" />
                          : <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[9px] font-bold ${selectionState === "needSize" ? "border-[#b8955a] text-[#b8955a]" : "border-[#ddd] text-[#ddd]"}`}>{hasColors ? 2 : 1}</span>}
                        Size
                        {hasColors && !selColor && <span className="font-['DM_Mono'] text-[9px] text-[#ccc] normal-case tracking-normal">(select colour first)</span>}
                      </span>
                      {selSize
                        ? <span className="font-['DM_Mono'] text-[12px] text-emerald-600 font-medium">{selSize} — {product.variants!.sizes.find((s) => s.value === selSize)?.stock_qty ?? 0} in stock ✓</span>
                        : <span className="font-['DM_Mono'] text-[12px] text-[#bbb]">Select size</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {product.variants!.sizes.map((s) => {
                        const combo = selColor
                          ? product.variants?.items?.find((it) => it.color === selColor && it.size === s.value)
                          : undefined;
                        const oos = combo !== undefined ? combo.stock_qty === 0 : s.stock_qty === 0;
                        const sel = selSize === s.value;
                        return (
                          <button
                            key={s.value}
                            onClick={() => { if (!oos) setSelSize(selSize === s.value ? "" : s.value); }}
                            className={`relative px-4 py-2 rounded-lg text-[12px] font-['DM_Mono'] border-2 transition-all ${
                              sel ? "border-[#1a1a1a] bg-[#1a1a1a] text-white shadow-md"
                              : oos ? "border-[#eee] text-[#ccc] cursor-not-allowed line-through"
                              : "border-[#ddd] text-[#333] hover:border-[#b8955a] hover:text-[#b8955a]"
                            }`}
                          >
                            {s.value}
                            {oos && <span className="absolute -top-1.5 -right-1.5 bg-red-400 text-white text-[8px] px-1 rounded">×</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Quantity ── */}
                {selectionState === "ready" && (
                  <div className="flex items-center justify-between border border-[#e8e4dd] rounded-xl px-4 py-3 mb-3">
                    <span className="font-['DM_Mono'] text-[13px] uppercase tracking-[0.12em] text-[#333]">Quantity</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                        className="w-8 h-8 rounded-full bg-white border border-black text-black flex items-center justify-center hover:bg-[#b8955a] hover:border-[#b8955a] hover:text-white transition-colors"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="font-['DM_Mono'] text-[15px] w-6 text-center">{qty}</span>
                      <button
                        onClick={() => setQty((q) => Math.min(Math.max(availableStock, 1), q + 1))}
                        className="w-8 h-8 rounded-full bg-white border border-black text-black flex items-center justify-center hover:bg-[#b8955a] hover:border-[#b8955a] hover:text-white transition-colors"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Stock warnings */}
                {selectionState === "ready" && availableStock <= 5 && availableStock > 0 && (
                  <p className="font-['DM_Mono'] text-[12px] text-black tracking-wide">Hurry! Only {availableStock} left </p>
                )}
                {selectionState === "ready" && availableStock === 0 && (
                   <p className="font-['DM_Mono'] text-[12px] text-black tracking-wide">Out of Stock</p>
                )}

                {/* Delivery / Returns */}
                {(cartItem.delivery || cartItem.returnPolicy) && (
                  <div className="space-y-1.5 mb-4">
                    {cartItem.delivery && (
                      <p className="font-['DM_Mono'] text-[11px] text-[#888] flex items-center gap-1.5">
                        <span className="text-[#b8955a]">🚚</span> {cartItem.delivery}
                      </p>
                    )}
                    {cartItem.returnPolicy && (
                      <p className="font-['DM_Mono'] text-[11px] text-[#888] flex items-center gap-1.5">
                        <span className="text-[#b8955a]">↩</span> {cartItem.returnPolicy}
                      </p>
                    )}
                  </div>
                )}

                {/* SKU / Material */}
                {(product.sku || product.material) && (
                  <div className="flex flex-wrap gap-3 mb-4 pt-3 border-t border-[#f0ebe3]">
                    {product.sku      && <span className="font-['DM_Mono'] text-[10px] text-[#aaa]">SKU: {product.sku}</span>}
                    {product.material && <span className="font-['DM_Mono'] text-[10px] text-[#aaa]">Material: {product.material}</span>}
                  </div>
                )}

                {/* Trust pillars */}
                <div className="grid grid-cols-3 gap-2 mb-5">
                  {[
                    { icon: <RefreshCw size={15} />, label: "Easy Returns" },
                    { icon: <Shield    size={15} />, label: "Secure Pay"   },
                    { icon: <Star      size={15} />, label: "Top Rated"    },
                  ].map((item) => (
                    <div key={item.label} className="border border-[#ece7df] rounded-xl p-2.5 text-center">
                      <div className="text-[#b8955a] flex justify-center mb-1">{item.icon}</div>
                      <p className="font-['DM_Mono'] text-[9px] uppercase tracking-[0.12em] text-[#555]">{item.label}</p>
                    </div>
                  ))}
                </div>

                {/* Flash message */}
                {actionMsg && (
                  <div className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-3 font-['DM_Mono'] text-[12px] ${
                    actionMsg.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"
                  }`}>
                    {actionMsg.ok ? <Check size={13} /> : <X size={13} />}
                    {actionMsg.text}
                  </div>
                )}
              </div>
            </div>

            {/* ── Sticky footer with action buttons ── */}
            <div className="shrink-0 border-t border-[#ece7df] bg-white px-4 md:px-8 py-4">
              <div className="flex flex-wrap gap-2.5 items-center">

                {/* Update Cart */}
                <button
                  onClick={handleUpdateCart}
                  disabled={saving || !canAct || !variantChanged}
                  className={`flex items-center gap-2 font-['DM_Mono'] text-[11px] tracking-[0.12em] uppercase px-5 py-3 rounded-xl transition-all flex-1 min-w-[140px] justify-center ${
                    !variantChanged ? "bg-[#f5f5f5] text-[#bbb] cursor-default border border-[#eee]"
                    : !canAct ? "bg-[#f1efe9] text-[#b5b2a9] cursor-not-allowed border border-[#eee]"
                    : "bg-neutral-900 text-white hover:bg-neutral-700"
                  }`}
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  {saving ? "Saving…" : variantChanged ? "Update Cart" : "No Changes"}
                </button>

                {/* Add as New Variant */}
                <button
                  onClick={handleAddAsNewVariant}
                  disabled={addingVariant || !canAct}
                  className={`flex items-center gap-2 font-['DM_Mono'] text-[11px] tracking-[0.12em] uppercase px-5 py-3 rounded-xl transition-all flex-1 min-w-[160px] justify-center ${
                    !canAct ? "bg-[#f1efe9] text-[#b5b2a9] cursor-not-allowed border border-[#eee]"
                    : "bg-[#051173] text-white hover:bg-[#C7AB5F]"
                  }`}
                >
                  {addingVariant ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBasket size={12} />}
                  {addingVariant ? "Adding…" : "Add as New Variant"}
                </button>

                {/* Buy Now */}
                <button
                  disabled={!canAct}
                  onClick={() => { if (canAct) window.location.href = "/user/checkout"; }}
                  className={`flex items-center gap-2 font-['DM_Mono'] text-[11px] tracking-[0.12em] uppercase px-4 py-3 rounded-xl border transition-all ${
                    !canAct ? "border-[#eee] text-[#ccc] cursor-not-allowed" : "border-[#e8e4dd] text-[#333] hover:border-[#b8955a] hover:text-[#b8955a]"
                  }`}
                >
                  Buy Now
                </button>

                
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');
        @keyframes cartModalIn {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
}